import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, ArrowLeft, ArrowRight } from 'lucide-react';
import { t, getCurrentLanguage } from '../utils/translations';
import api from '../components/api';
import { CardLoader } from '../components/Loader';
import Navbar from '../components/Navabar';
import Footer from '../components/Footer';

const ActualitePage = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalArticles, setTotalArticles] = useState(0);
  const articlesPerPage = 9;
  
  // États pour la recherche et filtres
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('latest'); // latest, oldest, title
  
  // États pour les statistiques
  const [stats, setStats] = useState({
    total_articles: 0,
    recent_articles: 0,
    articles_by_category: []
  });
  
  // Fonction pour nettoyer le HTML et tronquer le texte
  const cleanAndTruncateHtml = (htmlText, maxLength = 150) => {
    if (!htmlText) return '';
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlText;
    const plainText = tempDiv.textContent || tempDiv.innerText || '';
    
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength).trim() + '...';
  };

  // Fonction pour traduire le contenu des articles
  const translateArticleContent = (article, field) => {
    if (!article) return '';
    
    const lang = currentLanguage;
    
    if (field === 'title') {
      if (lang === 'en' && article.title_en) {
        return article.title_en;
      }
      return article.title_fr || article.title || '';
    }
    
    if (field === 'description') {
      if (lang === 'en' && article.description_en) {
        return article.description_en;
      }
      return article.description_fr || article.description || '';
    }
    
    return article[field] || '';
  };

  // Écouter les changements de langue
  useEffect(() => {
    const handleLanguageChange = () => {
      const newLang = getCurrentLanguage();
      setCurrentLanguage(newLang);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, []);

  // Récupérer les catégories et statistiques
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/api/categories/');
        setCategories(response.data);
      } catch (err) {
        console.error('Erreur lors de la récupération des catégories:', err);
      }
    };

    const fetchStats = async () => {
      try {
        const response = await api.get('/api/articles/stats/');
        setStats(response.data);
      } catch (err) {
        console.error('Erreur lors de la récupération des statistiques:', err);
      }
    };

    fetchCategories();
    fetchStats();
  }, []);

  // Récupérer les articles avec pagination et filtres
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        
        // Construire les paramètres de requête
        const params = {
          page: currentPage,
          page_size: articlesPerPage,
          search: searchQuery,
          category: selectedCategory,
          sort: sortBy === 'latest' ? '-published_at' : 
                sortBy === 'oldest' ? 'published_at' : 
                sortBy === 'title' ? 'title_fr' : '-published_at'
        };

        const response = await api.get('/api/articles/', { params });
        
        if (response.data && response.data.results) {
          setArticles(response.data.results);
          setTotalPages(response.data.pagination.total_pages);
          setTotalArticles(response.data.pagination.total_count);
        } else {
          setArticles([]);
          setTotalPages(1);
          setTotalArticles(0);
        }
      } catch (err) {
        console.error('Erreur lors de la récupération des articles:', err);
        setArticles([]);
        setTotalPages(1);
        setTotalArticles(0);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [currentPage, searchQuery, selectedCategory, sortBy]);

  // Réinitialiser la page quand on change les filtres
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, sortBy]);

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#089556] via-[#07D1EB] to-[#0ba8c7] overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t('news.title')}
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8 leading-relaxed">
              {t('news.subtitle')}
            </p>
            
            {/* Statistiques */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="text-3xl font-bold text-white mb-2">{stats.total_articles}</div>
                <div className="text-white/80 text-sm">Total Articles</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="text-3xl font-bold text-white mb-2">{stats.recent_articles}</div>
                <div className="text-white/80 text-sm">Articles Récents (30j)</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="text-3xl font-bold text-white mb-2">{categories.length}</div>
                <div className="text-white/80 text-sm">Catégories</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search and Filters Section */}
      <section className="py-8 bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
            
            {/* Search Bar */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('news.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#089556] focus:border-transparent"
              />
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              
              {/* Category Filter */}
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="pl-10 pr-8 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#089556] focus:border-transparent appearance-none bg-white"
                >
                  <option value="">{t('news.allCategories')}</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {currentLanguage === 'en' ? category.libelle_en : category.libelle_fr}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sort Filter */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#089556] focus:border-transparent bg-white"
              >
                <option value="latest">{t('news.sortLatest')}</option>
                <option value="oldest">{t('news.sortOldest')}</option>
                <option value="title">{t('news.sortTitle')}</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Articles Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          
          {/* Results Info */}
          <div className="mb-8">
            <p className="text-gray-600">
              {t('news.resultsCount', { count: totalArticles })}
              {searchQuery && ` ${t('news.forQuery')} "${searchQuery}"`}
              {selectedCategory && ` ${t('news.inCategory')} "${categories.find(c => c.id == selectedCategory)?.libelle_fr || categories.find(c => c.id == selectedCategory)?.libelle_en}"`}
            </p>
          </div>

          {/* Articles Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, index) => (
                <CardLoader key={index} />
              ))}
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-16">
              <div className="max-w-md mx-auto">
                <div className="h-16 w-16 text-gray-400 mx-auto mb-4">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {t('news.noArticlesFound')}
                </h3>
                <p className="text-gray-600 mb-6">
                  {t('news.noArticlesDesc')}
                </p>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('');
                    setSortBy('latest');
                  }}
                  className="bg-[#089556] text-white px-6 py-2 rounded-lg hover:bg-[#078a4a] transition-colors"
                >
                  {t('news.clearFilters')}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {articles.map((article) => {
                  const translatedTitle = translateArticleContent(article, 'title');
                  const translatedDescription = translateArticleContent(article, 'description');
                  const translatedCategory = article.category_info ? 
                    (currentLanguage === 'en' ? article.category_info.libelle_en : article.category_info.libelle_fr) : 
                    'Article';

                  return (
                    <article
                      key={article.id}
                      className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
                    >
                      {/* Image */}
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={article.cover_image_url || article.cover_image || '/images/article-default.jpg'}
                          alt={translatedTitle}
                          className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                          onError={(e) => {
                            e.target.src = '/images/article-default.jpg';
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                        
                        {/* Category Badge */}
                        <div className="absolute top-4 left-4">
                          <span className="text-xs text-white bg-[#089556]/90 backdrop-blur-sm px-3 py-1 rounded-full shadow-sm font-medium">
                            {translatedCategory}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-6">
                        <h3 className="text-xl font-bold text-gray-800 mb-3 line-clamp-2 hover:text-[#089556] transition-colors">
                          {translatedTitle}
                        </h3>
                        
                        <p className="text-gray-600 text-sm leading-relaxed mb-6 line-clamp-3">
                          {translatedDescription ? cleanAndTruncateHtml(translatedDescription, 150) : t('news.noDescription')}
                        </p>

                        {/* Read More Button */}
                        <Link
                          to={`/${currentLanguage}/actualite/${article.slug}`}
                          className="inline-flex items-center justify-center bg-gradient-to-r from-[#089556] to-[#07D1EB] hover:from-[#07D1EB] hover:to-[#089556] text-white px-6 py-2 rounded-lg text-sm font-medium transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                        >
                          {t('news.readMore')}
                          <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                      </div>
                    </article>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-12 flex justify-center">
                  <nav className="flex items-center space-x-2">
                    {/* Previous Button */}
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="flex items-center px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ArrowLeft className="h-4 w-4 mr-1" />
                      {t('news.previous')}
                    </button>

                    {/* Page Numbers */}
                    {[...Array(totalPages)].map((_, index) => {
                      const pageNumber = index + 1;
                      const isCurrentPage = pageNumber === currentPage;
                      const isNearCurrent = Math.abs(pageNumber - currentPage) <= 2;
                      
                      if (isNearCurrent || pageNumber === 1 || pageNumber === totalPages) {
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => setCurrentPage(pageNumber)}
                            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                              isCurrentPage
                                ? 'bg-[#089556] text-white'
                                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      } else if (pageNumber === 2 && currentPage > 4) {
                        return <span key="ellipsis1" className="px-2 text-gray-500">...</span>;
                      } else if (pageNumber === totalPages - 1 && currentPage < totalPages - 3) {
                        return <span key="ellipsis2" className="px-2 text-gray-500">...</span>;
                      }
                      return null;
                    })}

                    {/* Next Button */}
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="flex items-center px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('news.next')}
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </button>
                  </nav>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />

      <style jsx>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </>
  );
};

export default ActualitePage;
